/**
 * usePermissions Hook
 * 
 * React hook for checking user permissions on resources.
 * Integrates with useAuth to provide permission-aware UI components.
 * 
 * @module hooks/usePermissions
 */

import { useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { 
  canPerform, 
  getAllowedActions,
  getPermissionDeniedReason,
  type Resource, 
  type Action,
  type AppRole,
} from '@/lib/permissions';

export interface PermissionResult {
  // Role info
  role: AppRole | null;
  isSuperAdmin: boolean;
  isOrgAdmin: boolean;
  
  // Common permission checks
  canCreate: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  
  // Staff-specific permissions
  canInvite: boolean;
  canChangeRole: boolean;
  canRemove: boolean;
  
  // All allowed actions
  allowedActions: Action[];
  
  // Helper functions
  can: (action: Action) => boolean;
  getDeniedReason: (action: Action) => string;
}

/**
 * Hook to check permissions for a specific resource
 * 
 * @example
 * const { canCreate, canDelete, getDeniedReason } = usePermissions('courts');
 * 
 * // In JSX:
 * {canCreate && <Button onClick={handleCreate}>New Court</Button>}
 * 
 * <Tooltip content={getDeniedReason('delete')}>
 *   <Button disabled={!canDelete}>Delete</Button>
 * </Tooltip>
 */
export function usePermissions(resource: Resource): PermissionResult {
  const {
    activeBusiness,
    platformRole,
    isOrgAdmin,
  } = useAuth();

  const role = activeBusiness?.role ?? null;
  const isSuperAdmin = platformRole === 'super_admin';
  
  return useMemo(() => {
    // Super admins and org owners have elevated access
    const hasElevatedAccess = isSuperAdmin || isOrgAdmin;
    
    // For elevated users, grant all permissions
    if (hasElevatedAccess) {
      return {
        role,
        isSuperAdmin,
        isOrgAdmin,
        canCreate: true,
        canRead: true,
        canUpdate: true,
        canDelete: true,
        canInvite: true,
        canChangeRole: true,
        canRemove: true,
        allowedActions: ['create', 'read', 'update', 'delete', 'invite', 'change_role', 'remove'],
        can: () => true,
        getDeniedReason: () => '',
      };
    }
    
    // Check permissions based on business role
    const allowedActions = getAllowedActions(role, resource);
    
    return {
      role,
      isSuperAdmin,
      isOrgAdmin,
      canCreate: canPerform(role, resource, 'create'),
      canRead: canPerform(role, resource, 'read'),
      canUpdate: canPerform(role, resource, 'update'),
      canDelete: canPerform(role, resource, 'delete'),
      canInvite: canPerform(role, resource, 'invite'),
      canChangeRole: canPerform(role, resource, 'change_role'),
      canRemove: canPerform(role, resource, 'remove'),
      allowedActions,
      can: (action: Action) => canPerform(role, resource, action),
      getDeniedReason: (action: Action) => getPermissionDeniedReason(resource, action),
    };
  }, [role, isSuperAdmin, isOrgAdmin, resource]);
}

/**
 * Hook to check if user has access to any of the given resources
 * Useful for showing/hiding entire navigation sections
 */
export function useHasAnyAccess(resources: Resource[]): boolean {
  const { activeBusiness, platformRole, isOrgAdmin } = useAuth();

  const role = activeBusiness?.role ?? null;
  const isSuperAdmin = platformRole === 'super_admin';
  
  return useMemo(() => {
    if (isSuperAdmin || isOrgAdmin) return true;
    if (!role) return false;
    
    return resources.some(resource => canPerform(role, resource, 'read'));
  }, [role, isSuperAdmin, isOrgAdmin, resources]);
}

/**
 * Hook to check if user can perform a specific action on any business
 * Useful for global actions like creating a new case
 */
export function useCanPerformGlobally(resource: Resource, action: Action): boolean {
  const { businessMemberships, platformRole, isOrgAdmin } = useAuth();

  const isSuperAdmin = platformRole === 'super_admin';
  
  return useMemo(() => {
    if (isSuperAdmin || isOrgAdmin) return true;
    
    return businessMemberships.some(bm => 
      bm.isActive && canPerform(bm.role, resource, action)
    );
  }, [businessMemberships, isSuperAdmin, isOrgAdmin, resource, action]);
}
